import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { RefreshOutlined } from '@mui/icons-material';
import styles from './ChatSidebar.module.scss';
import { getAiChatHistory } from '../../services/aiChatService';

// Format date in a human-readable way
const formatRelativeDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Today - show time
    if (diffDays === 0) {
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        return `${diffHours}h ago`;
    }

    // Yesterday
    if (diffDays === 1) return 'Yesterday';

    // Within a week
    if (diffDays < 7) return `${diffDays} days ago`;

    // Older - show date
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ITEMS_PER_PAGE = 10;

const ChatSidebar = ({ onSelect, onNewChat, selectedId }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);

    const fetchHistory = async (currentOffset = 0, append = false) => {
        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const response = await getAiChatHistory(currentOffset, ITEMS_PER_PAGE);
            const data = response.data;

            let conversations = [];
            if (data && data.conversations && Array.isArray(data.conversations)) {
                conversations = data.conversations;
            } else if (Array.isArray(data)) {
                conversations = data;
            }

            // Check if there are more items to load
            setHasMore(conversations.length === ITEMS_PER_PAGE);

            if (append) {
                setHistory(prev => [...prev, ...conversations]);
            } else {
                setHistory(conversations);
            }
        } catch (error) {
            console.error("Failed to fetch chat history:", error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchHistory(0, false);
    }, []);

    const handleLoadMore = () => {
        const newOffset = offset + ITEMS_PER_PAGE;
        setOffset(newOffset);
        fetchHistory(newOffset, true);
    };

    const handleRefresh = () => {
        setOffset(0);
        fetchHistory(0, false);
    };

    const handleNewChat = () => {
        // Refresh the list and call the parent's onNewChat
        handleRefresh();
        if (onNewChat) {
            onNewChat();
        }
    };

    return (
        <div className={styles.sidebarContainer}>
            <div className={styles.header} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                        Chat History
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleRefresh}
                        disabled={loading}
                        sx={{
                            padding: '4px',
                            color: '#888',
                            '&:hover': { color: '#1976d2' }
                        }}
                        title="Refresh"
                    >
                        <RefreshOutlined sx={{ fontSize: '18px' }} />
                    </IconButton>
                </div>
                <Typography
                    variant="caption"
                    onClick={handleNewChat}
                    sx={{
                        cursor: 'pointer',
                        color: '#1976d2',
                        fontWeight: 'bold',
                        '&:hover': { textDecoration: 'underline' }
                    }}
                >
                    + New
                </Typography>
            </div>
            <div className={styles.list}>
                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                        Loading...
                    </div>
                ) : history.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                        No history found
                    </div>
                ) : (
                    history.map((item) => {
                        // Derive title from the first user message
                        const firstUserMessage = item.messages?.find(m => m.role === 'user');
                        const title = firstUserMessage?.content || `Conversation ${item.conversationId?.substring(0, 6)}`;
                        // Use conversation createdAt or last message timestamp
                        const date = item.createdAt || item.messages?.[item.messages.length - 1]?.timestamp;
                        const isSelected = selectedId === item.conversationId;

                        return (
                            <div
                                key={item.conversationId}
                                className={styles.item}
                                onClick={() => onSelect && onSelect(item)}
                                style={{ backgroundColor: isSelected ? '#e3f2fd' : 'transparent' }}
                            >
                                <div className={styles.title}>{title}</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div className={styles.date}>{formatRelativeDate(date)}</div>
                                    <div style={{ fontSize: '9px', color: '#bbb', fontFamily: 'monospace' }}>{item.conversationId}</div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            {!loading && hasMore && (
                <div style={{ padding: '10px', borderTop: '1px solid #f0f0f0' }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        sx={{
                            width: '100%',
                            textTransform: 'none',
                            color: '#1976d2',
                            fontSize: '13px'
                        }}
                    >
                        {loadingMore ? 'Loading...' : 'Load More'}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ChatSidebar;
